import React, { useState, useEffect } from "react";

// Shows species evidence for a submission in the Admin ProductDetails page.
// Each evidence row shows species, type, and a download link for files
// or a clickable URL for links.

export default function SpeciesEvidencePanel({ submissionId }) {
  const [evidence, setEvidence] = useState([]);
  const [loading, setLoading]   = useState(true);

  useEffect(() => {
    if (!submissionId) return;
    setLoading(true);
    fetch(`https://dshb-deposits.its.uiowa.edu/api/species-evidence/${submissionId}`)
      .then(r => r.json())
      .then(data => { setEvidence(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [submissionId]);

  // The backend requires an Authorization: Bearer <token> header on every
  // route (see backend/auth.js), which src/utils/apiAuth.js attaches
  // automatically to every fetch() call - but only to fetch() calls. A
  // plain <a href> is a browser navigation, not a fetch(), so it bypasses
  // that interceptor entirely and always hits the server with no token,
  // hence "Not authenticated". Same fetch-blob-download pattern as Admin's
  // ProductDetails.js downloadProtocolFile(), which already works fine for
  // exactly this reason.
  const handleDownload = async (evidenceId, fileName) => {
    try {
      const res = await fetch(`https://dshb-deposits.its.uiowa.edu/api/species-evidence/download/${evidenceId}`);
      if (!res.ok) { alert("Failed to download file."); return; }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = fileName || `evidence_${evidenceId}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert("Failed to download file: " + err.message);
    }
  };

  if (loading) return <p style={{ color: "#9ca3af", fontSize: "13px" }}>Loading evidence…</p>;

  if (evidence.length === 0) return (
    <div style={{ padding: "16px", background: "#fefce8", border: "1px solid #fde68a", borderRadius: "8px", fontSize: "13px", color: "#854d0e" }}>
      ⚠️ No reactivity evidence submitted for this antibody.
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      {evidence.map(ev => (
        <div key={ev.evidence_id} style={{
          border: "1px solid #e2e8f0", borderRadius: "8px", overflow: "hidden",
        }}>
          <div style={{
            display: "flex", alignItems: "center", justifyContent: "space-between",
            padding: "10px 14px", background: "#f8fafc",
            borderBottom: "1px solid #e2e8f0",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <span style={{ fontWeight: 700, fontSize: "13px", color: "#1a2c5b" }}>
                {ev.species}
              </span>
              <span style={{
                fontSize: "10px", fontWeight: 700, padding: "2px 8px",
                background: "#e0e7ff", color: "#3730a3", borderRadius: "20px",
              }}>
                {ev.evidence_type === "file" ? "📄 File" : "🔗 Link"}
              </span>
              {ev.lab_confirmed === 1 && (
                <span style={{
                  fontSize: "10px", fontWeight: 700, padding: "2px 8px",
                  background: "#dcfce7", color: "#15803d", borderRadius: "20px",
                }}>✓ Lab confirmed</span>
              )}
            </div>
            <span style={{ fontSize: "11px", color: "#9ca3af" }}>
              {ev.created_at ? new Date(ev.created_at).toLocaleDateString() : ""}
            </span>
          </div>
          <div style={{ padding: "10px 14px", fontSize: "13px" }}>
            {ev.evidence_type === "file" ? (
              <button
                onClick={() => handleDownload(ev.evidence_id, ev.file_name)}
                style={{
                  color: "#305DBD", fontWeight: 600, textDecoration: "none",
                  background: "none", border: "none", padding: 0, cursor: "pointer",
                  font: "inherit",
                }}
              >
                📄 {ev.file_name}
              </button>
            ) : (
              <div>
                <a href={ev.link_url} target="_blank" rel="noreferrer"
                  style={{ color: "#305DBD", fontWeight: 600, wordBreak: "break-all" }}>
                  🔗 {ev.link_label || ev.link_url}
                </a>
                {ev.link_label && ev.link_url !== ev.link_label && (
                  <p style={{ margin: "4px 0 0", fontSize: "11px", color: "#9ca3af" }}>
                    {ev.link_url}
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}